import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/internal/operators/map";
import { AutomapperService } from "../../../core/automapper/automapper.service";
import { BASE_API_URL } from "../../../core/environment.tokens";
import { MemberChartM360Response } from "./member-chart-response.model";
import { MemberDataLoadState } from "./member-data-load-state-modal";
import { LoadSummaryResponse } from "./member-dataload-summary.model";
import { IMemberHistoryResponse } from "./member-history-response.model";
import { MemberItem } from "./member-item.model";
import { MemberM360Response } from "./member-response.model";

@Injectable({
  providedIn: "root",
})
export class MemberV2Service {
  state = new BehaviorSubject<MemberDataLoadState>(new MemberDataLoadState());

  constructor(
    @Inject(BASE_API_URL) private readonly baseApiUrl: string,
    private http: HttpClient,
    private automapper: AutomapperService
  ) { }

  setState(data: Partial<MemberDataLoadState>): void {
    const newData = new MemberDataLoadState({ ...this.state.value, ...data });
    this.state.next(newData);
  }

  resetState(data: Partial<MemberDataLoadState>): void {
    this.state.next(new MemberDataLoadState(data));
  }

  clearState(): void {
    this.state.next(new MemberDataLoadState());
  }

  searchMembers(searchRequest: any, isSearch: boolean = false): Observable<MemberM360Response> {
    const endpoint = isSearch ? "search" : "query";
    const url = `${this.baseApiUrl}m360/members/${endpoint}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.post(url, searchRequest, { headers }).pipe(
      map(this.automapper.curry("default", "MemberM360Response"))
    );
  }

  listPotentialDuplicates(searchRequest: any): Observable<MemberM360Response> {
    const url = `${this.baseApiUrl}m360/members/potentialduplicates`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.post(url, searchRequest, { headers }).pipe(
      map(this.automapper.curry("default", "MemberM360Response"))
    );
  }

  resolveDuplicateMembers(request: any): Observable<any> {
    const url = `${this.baseApiUrl}m360/members/resolve-duplicates`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.post(url, request, { headers });
  }

  mergeDuplicateMembers(request: any): Observable<any> {
    const url = `${this.baseApiUrl}m360/members/merge-duplicates`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.post(url, request, { headers });
  }

  getPotentialDuplicates(memberRecordId: string): Observable<MemberItem[]> {
    const url = `${this.baseApiUrl}m360/members/get-potential-duplicates?memberRecordId=${memberRecordId}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.get(url, { headers }).pipe(
      map(this.automapper.curryMany("default", "MemberItem"))
    );
  }

  getMemberHistory(memRecordId: string): Observable<IMemberHistoryResponse> {
    const url = `${this.baseApiUrl}m360/members/${encodeURIComponent(memRecordId)}/history`;
    return this.http.get<IMemberHistoryResponse>(url);
  }

  getMemberCharts(request: any, includeMerged: boolean = false, includeConflicts: boolean = false): Observable<MemberChartM360Response> {
    const url = `${this.baseApiUrl}m360/members/chart/query` +
      `?includeMergedMemberCharts=${includeMerged}` +
      `&includeConflictMemberCharts=${includeConflicts}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.post(url, request, { headers }).pipe(
      map(this.automapper.curry("default", "MemberChartM360Response"))
    );
  }

  getMemberById(memberId: string, includeMerged: boolean = false, includeConflicts: boolean = false): Observable<any> {
    const headers = new HttpHeaders().set("content-type", "application/json");
    const params = new URLSearchParams();
    params.set("includeConflicts", includeConflicts.toString());
    params.set("includeMerged", includeMerged.toString());
    params.set("memberId", memberId);
    const url = `${this.baseApiUrl}m360/members/memberrecord?${params.toString()}`;
    return this.http.get(url, { headers });
  }

  getPresignedUrl(fileName: string): Observable<{ loadId: string; presignedUrl: string }> {
    const url = `${this.baseApiUrl}m360/bulk-load/presignedurl`;

    return this.http.post(url, { fileName, contentType: "text/plain" }, { headers: new HttpHeaders({ "Content-Type": "application/json" }) })
      .pipe(map((response: any) => response));
  }

  submitBulkLoadFile(data: { fileName: string; loadId: string }): Observable<{ loadId: string; presignedUrl: string }> {
    const url = `${this.baseApiUrl}m360/bulk-load/submit-file`;

    return this.http.post(url, data, { headers: new HttpHeaders({ "Content-Type": "application/json" }) })
      .pipe(map((response: any) => response)
      );
  }

  getMemberBulkLoadStatus(loadId: string): Observable<any> {
    const url = `${this.baseApiUrl}m360/bulk-load/status?loadId=${loadId}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.get(url, { headers }).pipe(map((response: any) => response));
  }


  getMemberBulkLoadSummary(loadId: string): Observable<LoadSummaryResponse> {
    const url = `${this.baseApiUrl}m360/bulk-load/summary?loadId=${loadId}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.get(url, { headers }).pipe(
      map(this.automapper.curry("default", "LoadSummaryResponse"))
    );
  }

  getMemberBulkLoadValidationResults(loadId: string): Observable<any> {
    const url = `${this.baseApiUrl}m360/bulk-load/validation-results?loadId=${loadId}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.get(url, { headers }).pipe(map((response: any) => response));
  }

  getMemberBulkLoadValidationReportUrl(loadId: string): Observable<string> {
    const url = `${this.baseApiUrl}m360/bulk-load/validation-report-url?loadId=${loadId}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.get(url, { headers }).pipe(map((response: any) => response));
  }

  submitBulkLoadMembers(data: { loadId: string }): Observable<any> {
    const url = `${this.baseApiUrl}m360/bulk-load/submit`;

    return this.http.post(url, data, { headers: new HttpHeaders({ "Content-Type": "application/json" }) })
      .pipe(map((response: any) => response)
      );
  }
}
